import { useEffect, useRef, useState } from "react";
import { AppearancePicker } from "../appearance/AppearancePicker";
import { ChevronDown, CreditCard, Eye, LifeBuoy, LogOut, MessageSquarePlus, Shield, UserRound } from "lucide-react";

/**
 * THE ACCOUNT MENU (spec §88A). The signed-in top bar used to carry five
 * equal buttons — Early Access, Send feedback, Admin, the person's name and
 * Sign out — competing with the navigation. They are one menu now, opened
 * from the person's initials, so the bar leads with where to go.
 */
export function initialsFor(name: string, email: string): string {
  const source = name && name !== email ? name : email.split("@")[0];
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  return (parts.length > 1 ? parts[0][0] + parts[1][0] : source.slice(0, 2)).toUpperCase();
}

export function AccountMenu({
  name,
  email,
  isAdmin,
  current,
  onAccount,
  onBilling,
  onAdmin,
  onFeedback,
  onPrivacy,
  onSignOut,
}: {
  name: string;
  email: string;
  isAdmin: boolean;
  current: string;
  onAccount: () => void;
  onBilling?: () => void;
  onAdmin: () => void;
  onFeedback: () => void;
  onPrivacy: () => void;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const pick = (action: () => void) => () => {
    setOpen(false);
    action();
  };
  const shown = name && name !== email ? name : email;
  return (
    <div className="account-menu" ref={ref}>
      <button type="button" className="account-menu-trigger" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="avatar" aria-hidden="true">
          {initialsFor(name, email)}
        </span>
        <span className="account-menu-name">{shown}</span>
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {open && (
        <div className="account-menu-list" role="menu">
          <div className="account-menu-head">
            <strong>{shown}</strong>
            <span>{email}</span>
          </div>
          <button role="menuitem" className={current === "account" ? "is-current" : ""} onClick={pick(onAccount)}>
            <UserRound size={16} aria-hidden="true" /> Account
          </button>
          {onBilling && (
            <button role="menuitem" className={current === "billing" ? "is-current" : ""} onClick={pick(onBilling)}>
              <CreditCard size={16} aria-hidden="true" /> Billing
            </button>
          )}
          {isAdmin && (
            <button role="menuitem" className={current === "admin" ? "is-current" : ""} onClick={pick(onAdmin)}>
              <Shield size={16} aria-hidden="true" /> Admin
            </button>
          )}
          <hr />
          <AppearancePicker />
          <button role="menuitem" onClick={pick(onFeedback)}>
            <MessageSquarePlus size={16} aria-hidden="true" /> Send feedback
          </button>
          <button role="menuitem" onClick={pick(onPrivacy)}>
            <Eye size={16} aria-hidden="true" /> What the app records
          </button>
          <hr />
          <button role="menuitem" onClick={pick(onSignOut)}>
            <LogOut size={16} aria-hidden="true" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export { LifeBuoy };
